// src/pages/ProjetDetailPage.tsx — SCE Issues
// Fiche d'un PN : ses équipements et les problèmes déclarés dessus.
//
// ─── Pourquoi cette fiche existe ───
//
// Le PN est la référence naturelle du chef de projet (cf. claude.md,
// « Hiérarchie métier »). Jusqu'ici l'application ne savait en parler qu'au
// travers d'un équipement : pour voir les autres machines du même projet, il
// fallait retourner à la liste et refaire une recherche en perdant sa place.
//
// Depuis un problème, un clic sur le PN ouvre donc cette fiche, qui répond à
// la question suivante : « qu'y a-t-il d'autre sur ce projet ? »
//
// ─── Aucune requête dédiée ───
//
// Les équipements viennent du cache mémoire de equipements.api.ts, déjà chargé
// par le formulaire de déclaration ou la liste.
//
// ⚠️ Les problèmes NE passent PAS par useIssues({ pn }). Ce filtre existe et
// part bien en OData sur field_8, mais en égalité EXACTE — or les PN de
// Windchill ne sont pas normalisés : « PN4334 » et « pn4334 » coexistent dans
// les exports réels. Un PN saisi dans une casse et déclaré dans l'autre ne
// remontait alors aucun problème, sans la moindre erreur pour le signaler.
//
// On charge donc la liste non filtrée — le même useIssues() que l'accueil, le
// dashboard et la fiche chef de projet, déjà en mémoire — et on rapproche
// côté client sur le PN normalisé.
//
// Contrepartie connue : getIssues plafonne à 200 éléments sans pagination. Au
// delà, ce rapprochement deviendra partiel et il faudra paginer — ce qui vaut
// déjà pour le dashboard et l'accueil, qui reposent sur le même chargement.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CadreFiche } from '@/components/layout/CadreFiche'
import { LienFiche } from '@/components/ui/LienFiche'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import {
  EtatVide,
  LigneCompacte,
  TitreSection,
} from '@/components/issues/LigneCompacte'
import { BoutonSuivreElement } from '@/components/issues/BoutonSuivreElement'
import { useIssues } from '@/hooks/useIssues'
import { listAllEquipements } from '@/api/equipements.api'
import {
  lienChefProjet,
  lienEquipement,
  lienProbleme,
} from '@/utils/liensFiches'
import { suiteDe } from '@/utils/suiteProblemes'
import {
  cleRapprochement,
  compteCommeProbleme,
  type Equipement,
} from '@/types/issue.types'
import { EtatProbleme } from '@/components/ui/EtatProbleme'

export function ProjetDetailPage(): JSX.Element {
  const navigate = useNavigate()
  const ouvrirFiche = useOuvrirFiche()
  const { pn } = useParams<{ pn: string }>()

  const [tous, setTous] = useState<Equipement[]>([])
  const [loading, setLoading] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)

  const {
    issues: tousProblemes,
    loading: loadingIssues,
    error: erreurProblemes,
  } = useIssues()

  useEffect(() => {
    let annule = false
    setLoading(true)
    setErreur(null)
    listAllEquipements()
      .then((eqs) => {
        if (!annule) setTous(eqs)
      })
      .catch((err: unknown) => {
        if (!annule) {
          setErreur(err instanceof Error ? err.message : 'Erreur de chargement')
        }
      })
      .finally(() => {
        if (!annule) setLoading(false)
      })
    return () => {
      annule = true
    }
  }, [])

  // Comparaison insensible à la casse et aux espaces de bord : les PN saisis
  // dans Windchill ne sont pas normalisés. La règle vit dans
  // `cleRapprochement` (issue.types.ts), pas dans ce fichier.
  const cible = cleRapprochement(pn)

  const equipements = useMemo(() => {
    if (!cible) return []
    return tous
      .filter((e) => cleRapprochement(e.pn) === cible)
      .sort((a, b) => a.numeroSerie.localeCompare(b.numeroSerie, 'fr'))
  }, [tous, cible])

  // Même normalisation que pour les équipements : c'est tout l'intérêt de
  // rapprocher ici plutôt que de laisser SharePoint comparer en exact.
  const issues = useMemo(
    () => tousProblemes.filter((i) => cleRapprochement(i.pn) === cible),
    [tousProblemes, cible],
  )

  // CVE et chef de projet sont censés être constants au sein d'un PN. On prend
  // la première valeur non vide sans forcer l'égalité : une incohérence de
  // données ne doit jamais empêcher la fiche de s'afficher.
  const cve = equipements.find((e) => e.cve)?.cve
  const chefProjet = equipements.find((e) => e.chefProjet)?.chefProjet
  // Client (Customer Name de Windchill, 20/10) : même règle, constant au sein d'un PN.
  const client = equipements.find((e) => e.client)?.client

  const titre = pn ?? 'Projet'

  if (loading) {
    return (
      <CadreFiche titre={titre} onBack={() => navigate('/equipements')}>
        <div className="p-4 flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg animate-pulse"
              style={{ height: 56, backgroundColor: 'var(--color-gray-10)' }}
            />
          ))}
        </div>
      </CadreFiche>
    )
  }

  if (erreur) {
    return (
      <CadreFiche titre={titre} onBack={() => navigate('/equipements')}>
        <div className="p-4">
          <EtatVide>{erreur}</EtatVide>
        </div>
      </CadreFiche>
    )
  }

  if (equipements.length === 0) {
    return (
      <CadreFiche titre={titre} onBack={() => navigate('/equipements')}>
        <div className="p-4">
          <EtatVide>Aucun équipement rattaché à ce projet.</EtatVide>
        </div>
      </CadreFiche>
    )
  }

  return (
    <CadreFiche titre={titre} onBack={() => navigate('/equipements')}>
      <div className="p-4 flex flex-col gap-5">
        {/* Identité du projet */}
        <section
          className="rounded-lg p-4"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
          }}
        >
          {/* Lot 12 bis : suivre le projet. Cloche seule, au même endroit que sur
              la fiche équipement (l'infobulle dit ce qu'elle fait). */}
          {pn && (
            <div className="flex justify-end" style={{ margin: '-6px -6px 0' }}>
              <BoutonSuivreElement genre="projet" id={pn} />
            </div>
          )}
          <Ligne label="PN" valeur={pn ?? '—'} />
          {cve && <Ligne label="CVE" valeur={cve} />}
          {chefProjet && (
            <Ligne
              label="Chef de projet"
              valeur={
                <LienFiche
                  vers={lienChefProjet(chefProjet)}
                  titre={`Voir les projets de ${chefProjet}`}
                >
                  {chefProjet}
                </LienFiche>
              }
            />
          )}
          <Ligne label="Équipements" valeur={String(equipements.length)} />
          {client && <Ligne label="Client" valeur={client} discret />}
        </section>

        {/* Équipements du projet */}
        <section>
          <TitreSection compteur={equipements.length}>Équipements</TitreSection>
          <div className="flex flex-col gap-2">
            {equipements.map((eq) => {
              // Même normalisation que pour le PN juste au-dessus : la
              // correction du 16/09 s'était arrêtée au PN et avait laissé
              // cette comparaison-ci en graphie brute, donc à zéro sur les
              // mêmes équipements.
              // Compteur : les annulés n'y entrent pas. La liste « Problèmes
              // déclarés » plus bas les garde, elle — c'est un historique, et
              // le badge Annulé dit ce qu'ils sont.
              const cleEq = cleRapprochement(eq.numeroSerie)
              const nb = issues.filter(
                (i) =>
                  compteCommeProbleme(i) &&
                  cleRapprochement(i.numeroSerie) === cleEq,
              ).length
              return (
                <LigneCompacte
                  key={eq.numeroSerie}
                  titre={eq.numeroSerie}
                  sousTitre={
                    [eq.familleEquipement, eq.of].filter(Boolean).join(' · ') ||
                    undefined
                  }
                  droite={nb > 0 ? `${nb} pb` : undefined}
                  couleurDroite={
                    nb > 1 ? 'var(--color-traffic-red)' : undefined
                  }
                  onClick={() => ouvrirFiche(lienEquipement(eq.numeroSerie))}
                />
              )
            })}
          </div>
        </section>

        {/* Problèmes déclarés sur le projet */}
        <section>
          <TitreSection compteur={issues.length}>
            Problèmes déclarés
          </TitreSection>

          {loadingIssues && issues.length === 0 && (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg animate-pulse"
                  style={{ height: 48, backgroundColor: 'var(--color-gray-10)' }}
                />
              ))}
            </div>
          )}

          {/* Une erreur de chargement ne doit PAS se déguiser en « aucun
              problème » : c'est exactement ce que faisait la version
              précédente, qui ignorait `error`. */}
          {erreurProblemes && <EtatVide>{erreurProblemes}</EtatVide>}

          {!loadingIssues && !erreurProblemes && issues.length === 0 && (
            <EtatVide>Aucun problème déclaré sur ce projet.</EtatVide>
          )}

          <div className="flex flex-col gap-2">
            {issues.map((issue) => (
              <LigneCompacte
                key={issue.id}
                titre={`${issue.id} — ${issue.titre}`}
                sousTitre={`${issue.numeroSerie} · ${issue.metier} · ${issue.criticite}`}
                // Le badge de statut, comme partout (check-up du Lot 8).
                droite={<EtatProbleme statut={issue.statut} criticite={issue.criticite} />}
                // « Problème suivant » parcourt les problèmes du projet.
                onClick={() => ouvrirFiche(lienProbleme(issue.id), suiteDe(`Problèmes du ${pn ?? ''}`, issues))}
              />
            ))}
          </div>
        </section>
      </div>
    </CadreFiche>
  )
}

// ─── Sous-composants ───

function Ligne({
  label,
  valeur,
  discret,
}: {
  label: string
  valeur: React.ReactNode
  /** Information de contexte (le client) : texte secondaire, sans graisse. */
  discret?: boolean
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span
        style={{
          fontSize: 'var(--font-size-info)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {label}
      </span>
      <span
        className="text-right"
        style={{
          fontSize: 'var(--font-size-copy2)',
          fontWeight: discret ? 400 : 500,
          color: discret ? 'var(--color-text-secondary)' : undefined,
        }}
      >
        {valeur}
      </span>
    </div>
  )
}
